import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Pagination,
  Stack
} from '@mui/material';
import {
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ordersAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';

const OrdersPage = () => {
  const { formatCurrency } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [paymentStatusUpdateOpen, setPaymentStatusUpdateOpen] = useState(false);
  const [orderToUpdate, setOrderToUpdate] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    search: ''
  });

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      status: '',
      notes: ''
    }
  });

  const { control: paymentControl, handleSubmit: handlePaymentSubmit, reset: resetPayment, formState: { errors: paymentErrors, isSubmitting: isPaymentSubmitting } } = useForm({
    defaultValues: {
      paymentStatus: '',
      transactionId: '',
      notes: ''
    }
  });

  useEffect(() => {
    fetchOrders();
  }, [currentPage, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await ordersAPI.getOrders(params);
      setOrders(response.data.orders);
      setTotalPages(response.data.pagination.totalPages);
      setTotalOrders(response.data.pagination.totalOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      console.log('Fetching order details for ID:', orderId);
      const response = await ordersAPI.getOrder(orderId);
      console.log('Order details response:', response.data);
      setSelectedOrder(response.data.order);
      setOrderDetailOpen(true);
      // Close menu but don't clear selectedOrder yet
      setMenuAnchor(null);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
      handleMenuClose();
    }
  };

  const handleStatusUpdate = (order) => {
    setOrderToUpdate(order);
    reset({
      status: order.status,
      notes: order.notes || ''
    });
    setStatusUpdateOpen(true);
    handleMenuClose();
  };

  const handlePaymentStatusUpdate = (order) => {
    setOrderToUpdate(order);
    resetPayment({
      paymentStatus: order.paymentInfo?.status || order.customerData?.paymentStatus || 'pending',
      transactionId: order.paymentInfo?.transactionId || '',
      notes: order.notes || ''
    });
    setPaymentStatusUpdateOpen(true);
    handleMenuClose();
  };

  const onStatusSubmit = async (data) => {
    try {
      await ordersAPI.updateOrderStatus(orderToUpdate._id, data.status, data.notes);
      toast.success('Order status updated successfully');
      setStatusUpdateOpen(false);
      setOrderToUpdate(null);
      reset();
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const onPaymentStatusSubmit = async (data) => {
    try {
      await ordersAPI.updatePaymentStatus(orderToUpdate._id, data.paymentStatus, data.transactionId, data.notes);
      toast.success('Payment status updated successfully');
      setPaymentStatusUpdateOpen(false);
      setOrderToUpdate(null);
      resetPayment();
      fetchOrders();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleMenuClick = (event, order) => {
    setMenuAnchor(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOrder(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'primary';
      case 'shipped': return 'secondary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      case 'refunded': return 'default';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'default';
      default: return 'default';
    }
  };



  if (loading && orders.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Orders
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage customer orders and track their status
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search orders..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                fullWidth
                size="small"
                placeholder="Order number, customer name, email..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Order Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="">All Payment Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ status: '', paymentStatus: '', search: '' });
                  setCurrentPage(1);
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {orders.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No orders found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filters.search || filters.status || filters.paymentStatus
                ? 'Try adjusting your filters to see more results'
                : 'Orders will appear here when customers place them through your websites'
              }
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {order.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {order.website.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.website.siteId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.totalItems} items
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.paymentStatus}
                        color={getPaymentStatusColor(order.paymentStatus)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(order.createdAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, order)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Showing {orders.length} of {totalOrders} orders
                </Typography>
              </Stack>
            </Box>
          )}
        </Card>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewOrder(selectedOrder?._id)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusUpdate(selectedOrder)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePaymentStatusUpdate(selectedOrder)}>
          <ListItemIcon>
            <PaymentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update Payment</ListItemText>
        </MenuItem>
      </Menu>

      {/* Order Detail Dialog */}
      <Dialog
        open={orderDetailOpen}
        onClose={() => {
          setOrderDetailOpen(false);
          setSelectedOrder(null);
        }}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: { xs: '100vh', sm: '90vh' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 'auto' },
            borderRadius: { xs: 0, sm: 1 },
            width: { xs: '100vw', sm: 'auto' },
            maxWidth: { xs: '100vw', sm: 'lg' }
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            borderBottom: { xs: '1px solid', sm: 'none' },
            borderColor: 'divider'
          }}
        >
          <Box>
            {/* Header Info */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Typography
                  variant={{ xs: "h6", sm: "h5" }}
                  component="div"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                    fontWeight: { xs: 500, sm: 400 }
                  }}
                >
                  Order #{selectedOrder?.orderNumber}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={{ xs: 0.5, sm: 1 }}
                  flexWrap="wrap"
                  sx={{
                    '& > *': {
                      mb: { xs: 0.5, sm: 0 }
                    }
                  }}
                >
                  <Chip
                    label={selectedOrder?.status}
                    color={getStatusColor(selectedOrder?.status)}
                    size="small"
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                      height: { xs: 24, sm: 32 }
                    }}
                  />
                  <Chip
                    label={`${selectedOrder?.paymentInfo?.status || selectedOrder?.customerData?.paymentStatus}`}
                    color={getPaymentStatusColor(selectedOrder?.paymentInfo?.status || selectedOrder?.customerData?.paymentStatus)}
                    size="small"
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                      height: { xs: 24, sm: 32 }
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      display: { xs: 'block', sm: 'inline' },
                      width: { xs: '100%', sm: 'auto' },
                      mt: { xs: 0.5, sm: 0 }
                    }}
                  >
                    {new Date(selectedOrder?.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Close button for mobile */}
              <IconButton
                onClick={() => {
                  setOrderDetailOpen(false);
                  setSelectedOrder(null);
                }}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  ml: 1
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>


          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            overflow: 'auto'
          }}
        >
          {selectedOrder ? (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={8}>
                  {/* Order Summary */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Order Summary
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Order Number:</strong> {selectedOrder.orderNumber}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Website:</strong> {selectedOrder.website?.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Order Date:</strong> {new Date(selectedOrder.createdAt).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Order Time:</strong> {new Date(selectedOrder.createdAt).toLocaleTimeString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Total Items:</strong> {selectedOrder.totalItems || selectedOrder.products?.reduce((sum, p) => sum + p.quantity, 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Total Amount:</strong> <span style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#1976d2' }}>{formatCurrency(selectedOrder.totals?.total || 0)}</span>
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Customer Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PersonIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Customer Information
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {Object.entries(selectedOrder.customerData || {})
                          .filter(([key, value]) => !['paymentMethod', 'paymentStatus'].includes(key) && value) // Filter out payment fields and empty values
                          .map(([key, value]) => (
                            <Grid item xs={12} sm={6} key={key}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value || 'N/A'}
                              </Typography>
                            </Grid>
                          ))}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Products */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <ShoppingCartIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Ordered Items
                        </Typography>
                      </Box>

                      {/* Enhanced Product Details Cards */}
                      {selectedOrder.products?.map((product, index) => {
                        // Define core fields that are already displayed in table
                        const coreFields = ['id', 'name', 'description', 'price', 'quantity', 'sku'];

                        // Get additional fields dynamically, excluding configurationDetails and selectedConfiguration object
                        const additionalFields = Object.entries(product).filter(([key, value]) =>
                          !coreFields.includes(key) &&
                          key !== 'configurationDetails' && // Exclude configurationDetails as requested
                          key !== 'selectedConfiguration' && // Exclude selectedConfiguration object itself
                          value !== null &&
                          value !== undefined &&
                          value !== '' &&
                          !(Array.isArray(value) && value.length === 0)
                        );

                        // Add selectedConfiguration fields if they exist
                        if (product.selectedConfiguration && typeof product.selectedConfiguration === 'object') {
                          Object.entries(product.selectedConfiguration).forEach(([configKey, configValue]) => {
                            if (configValue !== null && configValue !== undefined && configValue !== '') {
                              additionalFields.push([`config_${configKey}`, configValue]);
                            }
                          });
                        }

                        return (
                          <Card key={index} variant="outlined" sx={{ mb: 2, backgroundColor: '#fafafa' }}>
                            <CardContent sx={{ pb: 2 }}>
                              <Grid container spacing={2}>
                                {/* Product Image */}
                                {product.image && (
                                  <Grid item xs={12} sm={3}>
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 120,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={product.image}
                                        alt={product.name}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          transition: 'transform 0.2s',
                                          '&:hover': {
                                            transform: 'scale(1.05)'
                                          }
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const parent = e.target.parentElement;
                                          if (parent) {
                                            parent.innerHTML = `
                                              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #999;">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                                </svg>
                                                <span style="font-size: 10px; margin-top: 4px;">Image unavailable</span>
                                              </div>
                                            `;
                                          }
                                        }}
                                        onLoad={(e) => {
                                          e.target.style.opacity = '1';
                                        }}
                                        style={{ opacity: 0 }}
                                      />
                                    </Box>
                                  </Grid>
                                )}

                                {/* Product Details */}
                                <Grid item xs={12} sm={product.image ? 9 : 12}>
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                    <Typography variant="h6" fontWeight="medium">
                                      {product.name}
                                    </Typography>
                                    <Box textAlign="right">
                                      <Typography variant="h6" color="primary">
                                        {formatCurrency(product.price * product.quantity)}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {formatCurrency(product.price)} × {product.quantity}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {product.description && (
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                      {product.description}
                                    </Typography>
                                  )}

                                  {/* Additional Product Information */}
                                  {additionalFields.length > 0 && (
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium" mb={1} color="primary">
                                        Product Details:
                                      </Typography>
                                      <Grid container spacing={1}>
                                        {additionalFields.map(([key, value]) => {
                                          // Format the field name
                                          let fieldName;
                                          if (key.startsWith('config_')) {
                                            // Handle selectedConfiguration fields
                                            const configKey = key.replace('config_', '');
                                            fieldName = configKey.charAt(0).toUpperCase() + configKey.slice(1).replace(/([A-Z])/g, ' $1');
                                          } else {
                                            fieldName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                                          }

                                          // Format the value based on type
                                          let displayValue;
                                          if (Array.isArray(value)) {
                                            displayValue = value.join(', ');
                                          } else if (typeof value === 'boolean') {
                                            displayValue = value ? 'Yes' : 'No';
                                          } else if (typeof value === 'number') {
                                            if (key === 'rating') {
                                              displayValue = `${value}/5 ⭐`;
                                            } else {
                                              displayValue = value.toString();
                                            }
                                          } else if (key === 'image') {
                                            return null; // Skip image field as it's already shown
                                          } else {
                                            displayValue = value.toString();
                                          }

                                          if (displayValue === null) return null;

                                          return (
                                            <Grid item xs={12} sm={6} md={4} key={key}>
                                              <Box
                                                sx={{
                                                  p: 1,
                                                  backgroundColor: 'white',
                                                  borderRadius: 1,
                                                  border: '1px solid #e0e0e0'
                                                }}
                                              >
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                  {fieldName}
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {displayValue}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  )}
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        );
                      })}

                      {/* Compact Table View */}
                      <Typography variant="body2" fontWeight="medium" mb={2} mt={3}>
                        Order Summary:
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Qty</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrder.products?.map((product, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={2}>
                                    {/* Small thumbnail in table */}
                                    {product.image && (
                                      <Box
                                        component="img"
                                        src={product.image}
                                        alt={product.name}
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                          border: '1px solid #e0e0e0',
                                          flexShrink: 0
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {product.name}
                                      </Typography>
                                      {product.description && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {product.description}
                                        </Typography>
                                      )}
                                      {product.sku && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          SKU: {product.sku}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(product.price)}
                                </TableCell>
                                <TableCell align="right">
                                  {product.quantity}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(product.price * product.quantity)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Order Totals */}
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        <Box minWidth={200}>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2">Subtotal:</Typography>
                            <Typography variant="body2">{formatCurrency(selectedOrder.totals?.subtotal || 0)}</Typography>
                          </Box>
                          {selectedOrder.totals?.tax > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">Tax:</Typography>
                              <Typography variant="body2">{formatCurrency(selectedOrder.totals.tax)}</Typography>
                            </Box>
                          )}
                          {selectedOrder.totals?.shipping > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">Shipping:</Typography>
                              <Typography variant="body2">{formatCurrency(selectedOrder.totals.shipping)}</Typography>
                            </Box>
                          )}
                          {selectedOrder.totals?.discount > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2">Discount:</Typography>
                              <Typography variant="body2" color="error">-{formatCurrency(selectedOrder.totals.discount)}</Typography>
                            </Box>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="h6">Total:</Typography>
                            <Typography variant="h6" color="primary">{formatCurrency(selectedOrder.totals?.total || 0)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Notes */}
                  {selectedOrder.notes && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Order Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.notes}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={4}>
                  {/* Payment Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PaymentIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Payment Information
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Payment Method:</strong> {selectedOrder.paymentInfo?.method || selectedOrder.customerData?.paymentMethod || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Payment Status:</strong>
                            <Chip
                              label={selectedOrder.paymentInfo?.status || selectedOrder.customerData?.paymentStatus || 'Unknown'}
                              color={getPaymentStatusColor(selectedOrder.paymentInfo?.status || selectedOrder.customerData?.paymentStatus)}
                              size="small"
                              sx={{ ml: 1, textTransform: 'capitalize' }}
                            />
                          </Typography>
                        </Grid>
                        {selectedOrder.paymentInfo?.transactionId && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Transaction ID:</strong> {selectedOrder.paymentInfo.transactionId}
                            </Typography>
                          </Grid>
                        )}
                        {selectedOrder.paymentInfo?.paidAt && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Paid At:</strong> {new Date(selectedOrder.paymentInfo.paidAt).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Contact Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <EmailIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Contact Information
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {selectedOrder.customerData?.email && (
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center">
                              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {selectedOrder.customerData.email}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedOrder.customerData?.phone && (
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center">
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {selectedOrder.customerData.phone}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {(selectedOrder.customerData?.address || selectedOrder.customerData?.city || selectedOrder.customerData?.state || selectedOrder.customerData?.zipCode) && (
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="flex-start">
                              <LocationIcon sx={{ fontSize: 16, mr: 1, mt: 0.5, color: 'text.secondary' }} />
                              <Box>
                                {selectedOrder.customerData?.address && (
                                  <Typography variant="body2" color="text.secondary">
                                    {selectedOrder.customerData.address}
                                  </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  {[
                                    selectedOrder.customerData?.city,
                                    selectedOrder.customerData?.state,
                                    selectedOrder.customerData?.zipCode
                                  ].filter(Boolean).join(', ')}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Order Status Timeline */}
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <ShippingIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Order Status
                        </Typography>
                      </Box>
                      <Box>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Chip
                            label={selectedOrder.status}
                            color={getStatusColor(selectedOrder.status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Updated:</strong> {new Date(selectedOrder.updatedAt).toLocaleString()}
                        </Typography>
                        {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                          <Box mt={2}>
                            <Typography variant="body2" fontWeight="medium" mb={1}>
                              Status History:
                            </Typography>
                            {selectedOrder.statusHistory.slice(-3).map((history, index) => (
                              <Box key={index} mb={1}>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(history.timestamp).toLocaleString()} - {history.status}
                                  {history.notes && ` (${history.notes})`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>No order data available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 1, sm: 2 },
            pt: 1,
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: { xs: '1px solid', sm: 'none' },
            borderColor: 'divider',
            zIndex: 1
          }}
        >
          <Box
            display="flex"
            gap={{ xs: 0.5, sm: 1 }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            sx={{
              '& .MuiButton-root': {
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                minHeight: { xs: 32, sm: 36 },
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            {/* Action Buttons - Same Row on Mobile */}
            <Box
              display="flex"
              gap={{ xs: 0.5, sm: 1 }}
              flexDirection="row"
              alignItems="center"
              flex={1}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setOrderDetailOpen(false);
                  handleStatusUpdate(selectedOrder);
                }}
                sx={{
                  minWidth: { xs: 'auto', sm: 'auto' },
                  flex: { xs: 1, sm: 'none' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: { xs: 0.5, sm: 1 } }}>
                  <EditIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1 }}>
                  <EditIcon fontSize="small" />
                </Box>
                <Box component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                  {/* Show short text on mobile, full text on desktop */}
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Status</Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Update Status</Box>
                </Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setOrderDetailOpen(false);
                  handlePaymentStatusUpdate(selectedOrder);
                }}
                sx={{
                  minWidth: { xs: 'auto', sm: 'auto' },
                  flex: { xs: 1, sm: 'none' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: { xs: 0.5, sm: 1 } }}>
                  <PaymentIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1 }}>
                  <PaymentIcon fontSize="small" />
                </Box>
                <Box component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Payment</Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Update Payment</Box>
                </Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.print()}
                sx={{
                  minWidth: { xs: 'auto', sm: 'auto' },
                  flex: { xs: 1, sm: 'none' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: { xs: 0.5, sm: 1 } }}>
                  <ReceiptIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1 }}>
                  <ReceiptIcon fontSize="small" />
                </Box>
                <Box component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>Print</Box>
              </Button>
            </Box>

            {/* Close Button */}
            <Button
              onClick={() => {
                setOrderDetailOpen(false);
                setSelectedOrder(null);
              }}
              variant="contained"
              size="small"
              sx={{
                minWidth: { xs: 'auto', sm: 'auto' },
                ml: { xs: 0.5, sm: 1 }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusUpdateOpen}
        onClose={() => {
          setStatusUpdateOpen(false);
          setOrderToUpdate(null);
          reset();
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onStatusSubmit)}>
          <DialogTitle>
            Update Order Status
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: 'Status is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Order Status</InputLabel>
                      <Select
                        {...field}
                        label="Order Status"
                        error={!!errors.status}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="shipped">Shipped</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes (Optional)"
                      fullWidth
                      multiline
                      rows={3}
                      helperText="Add any notes about this status update"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setStatusUpdateOpen(false);
              setOrderToUpdate(null);
              reset();
            }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={20} /> : 'Update Status'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Payment Status Update Dialog */}
      <Dialog
        open={paymentStatusUpdateOpen}
        onClose={() => {
          setPaymentStatusUpdateOpen(false);
          setOrderToUpdate(null);
          resetPayment();
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handlePaymentSubmit(onPaymentStatusSubmit)}>
          <DialogTitle>
            Update Payment Status
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="paymentStatus"
                  control={paymentControl}
                  rules={{ required: 'Payment status is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Payment Status</InputLabel>
                      <Select
                        {...field}
                        label="Payment Status"
                        error={!!paymentErrors.paymentStatus}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="transactionId"
                  control={paymentControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Transaction ID (Optional)"
                      fullWidth
                      helperText="Enter payment transaction ID if available"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={paymentControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes (Optional)"
                      fullWidth
                      multiline
                      rows={3}
                      helperText="Add any notes about this payment status update"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setPaymentStatusUpdateOpen(false);
              setOrderToUpdate(null);
              resetPayment();
            }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPaymentSubmitting}
            >
              {isPaymentSubmitting ? <CircularProgress size={20} /> : 'Update Payment Status'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default OrdersPage; 